import { useCallback, useState } from 'react';
import {
	Identifier,
	useGetList,
	useGetMany,
	useRecordContext,
	useUpdate
} from 'react-admin';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Chip, Menu, MenuItem, Stack } from '@mui/material';
import { TagChip } from '../../../tags/TagChip';
import { TagCreateModal } from '../../../tags/TagCreateModal';
import { Contact, Tag } from '../../../types';

export const TagsList = ({ resource }: { resource: 'properties' }) => {
	const record = useRecordContext<Contact>();
	const [open, setOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const { data: allTags, isPending: isPendingAllTags } = useGetList<Tag>(
		'objectTags',
		{
			pagination: { page: 1, perPage: 50 },
			sort: { field: 'name', order: 'ASC' }
		}
	);

	const { data: tags, isPending: isPendingRecordTags } = useGetMany<Tag>(
		'objectTags',
		{ ids: record?.tags },
		{ enabled: record && record.tags && record.tags.length > 0 }
	);
	const [update] = useUpdate();

	const unselectedTags =
		allTags && record && record.tags?.length
			? allTags.filter(tag => !record.tags?.includes(tag.id))
			: allTags;

	const handleMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleTagAdd = (id: Identifier) => {
		if (!record) {
			throw new Error('No object record found');
		}
		const tags = [...(record.tags || []), id];
		update(resource, {
			id: record.id,
			data: { tags },
			previousData: record
		});
	};

	const handleTagDelete = async (id: Identifier) => {
		if (!record) {
			throw new Error('No object record found');
		}
		const tags = record.tags.filter(tagId => tagId !== id);
		await update(resource, {
			id: record.id,
			data: { tags },
			previousData: record
		});
	};

	const openTagCreateDialog = () => {
		setOpen(true);
		setAnchorEl(null);
	};

	const handleTagCreateClose = () => {
		setOpen(false);
	};

	const handleTagCreated = useCallback(
		async (tag: Tag) => {
			debugger;
			if (!record) {
				throw new Error('No object record found');
			}

			await update(
				resource,
				{
					id: record.id,
					data: { tags: [...(record.tags || []), tag.id] },
					previousData: record
				},
				{
					onSuccess: () => {
						setOpen(false);
					}
				}
			);
		},
		[record, update, resource]
	);

	if (isPendingRecordTags || isPendingAllTags) return null;
	return (
		<>
			<Stack direction="row" gap="0 8px" flexWrap="wrap">
				{tags?.map(tag => (
					<Box mb={1} key={tag.id}>
						<TagChip
							tag={tag}
							onUnlink={() => handleTagDelete(tag.id)}
							key={tag.id}
							resource="objectTags"
						/>
					</Box>
				))}
			</Stack>
			<Box>
				<Chip
					icon={<ControlPointIcon />}
					size="small"
					variant="outlined"
					onClick={handleMenuOpen}
					label="Add tag"
					color="primary"
				/>
			</Box>
			<Menu
				open={Boolean(anchorEl)}
				onClose={handleMenuClose}
				anchorEl={anchorEl}
				sx={{
					maxHeight: 500
				}}
			>
				{unselectedTags?.map(tag => (
					<MenuItem
						sx={{ display: 'flex', justifyContent: 'space-between' }}
						key={tag.id}
						onClick={() => handleTagAdd(tag.id)}
						dense
					>
						<Chip
							size="small"
							variant="outlined"
							label={tag.name}
							style={{
								backgroundColor: tag.color,
								border: 0
							}}
							onClick={() => handleTagAdd(tag.id)}
						/>
					</MenuItem>
				))}
				<MenuItem onClick={openTagCreateDialog}>
					<Chip
						icon={<EditIcon />}
						size="small"
						variant="outlined"
						onClick={openTagCreateDialog}
						color="primary"
						label="Create new tag"
					/>
				</MenuItem>
			</Menu>
			<TagCreateModal
				open={open}
				onClose={handleTagCreateClose}
				onSuccess={handleTagCreated}
				resource="objectTags"
			/>
		</>
	);
};
